import React, { useState } from 'react';
import moment from 'moment';

import { Typography, Grid } from '@mui/material';
import {
  GridCallbackDetails,
  GridColumns,
  GridSortModel
} from '@mui/x-data-grid';

import CardLoader from '../../../../../../../components/CardLoader';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { useGetCustomerAccountBalance } from '@/api/finance/staff';
import type { StaffGetCustomerAccountBalanceItemResponse } from '@/api/finance/finance-api.schemas';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import { getOrderBy } from '@/RestoreComponent/DataGrid/utils';

export default function AccountBalance(props: any) {
  const { customerAllData } = props;
  const [accountBalance, setAccountBalance] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' }
  ]);
  const [loading, setLoading] = useState<boolean>(false);

  const getBalance = async () => {
    setLoading(true);
    await formDataInstance
      .get(
        `${CONFIG.USERS_API_URL}/staff/users?customerId=${customerAllData?.id}`
      )
      .then(response => {
        setAccountBalance(response.data.data.accountBalance);
      })
      .catch(e => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { data: balanceData, isLoading: accountTransactionLoading } =
    useGetCustomerAccountBalance({
      customerId: customerAllData?.id,
      pageSize: rowsPerPage,
      offset: page * rowsPerPage,
      orderBy: getOrderBy(sortModel)
    });

  const transactions =
    (balanceData?.data as unknown as StaffGetCustomerAccountBalanceItemResponse[]) ??
    [];

  React.useEffect(() => {
    getBalance();
  }, []);

  const setPageSize = (pageSize: number, _details: GridCallbackDetails) => {
    setRowsPerPage(pageSize);
    setPage(0);
  };

  const sortChange = (model: GridSortModel) => {
    setPage(0);
    if (model.length === 0) {
      const currentOptions = sortModel[0];
      if (currentOptions && typeof currentOptions.field === 'string') {
        const nextOptions: GridSortModel = [
          {
            field: currentOptions.field,
            sort: currentOptions.sort === 'asc' ? 'desc' : 'asc'
          }
        ];
        setSortModel(nextOptions);
      }
    } else {
      setSortModel(model);
    }
  };

  const columnConfig: GridColumns = [
    {
      field: 'created_at',
      headerName: 'Date',
      flex: 2,
      renderCell: ({ row }) =>
        moment(row.createdAt).format('MMM DD YYYY, hh:mm A')
    },
    {
      field: 'auth_user',
      headerName: 'Staff',
      flex: 2,
      renderCell: ({ row }) => row.authUser?.name
    },
    {
      field: 'acct_type',
      headerName: 'Type',
      flex: 0.6,
      sortable: false,
      renderCell: ({ row }) => (row.amount > 0 ? 'Credit' : 'Debit')
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => row.amount?.toFixed(2)
    },
    {
      field: 'reason_code',
      headerName: 'Reason',
      flex: 2,
      renderCell: ({ row }) => row.reasonCode
    }
  ];

  const rows = transactions?.data || [];

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px'
            }}
          >
            <Typography variant="subtitle1">
              Current Account Balance:&nbsp;&nbsp;
              {loading ? (
                <CardLoader size="small" />
              ) : (
                `$${accountBalance?.toFixed(2) || 0}`
              )}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <RestoreDataGrid
            rowCount={rows.length + (rows.length === rowsPerPage ? 1 : 0)}
            columnConfig={columnConfig}
            rows={rows}
            loading={accountTransactionLoading}
            page={page}
            pageSize={rowsPerPage}
            pageSizes={[10, 50, 100]}
            sortModel={sortModel}
            onPageChange={setPage}
            onPageChangeSize={setPageSize}
            onSortModelChange={sortChange}
          />
        </Grid>
      </Grid>
    </>
  );
}
